import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Users,
  Loader2,
  Search,
  AlertCircle,
  UserCheck,
  UserX,
  Clock,
  CheckCircle2,
  XCircle,
  Mail,
  Phone,
  Calendar,
  Building2,
  Bot,
  KeyRound,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useTimezone } from '@/contexts/TimezoneContext';
import { validateCPF, formatCPF, cleanCPF } from '@/utils/cpf';
import ResponsiveDataList from '@/components/responsive/ResponsiveDataList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to extract error message from API responses (handles Pydantic validation errors)
const getErrorMessage = (error, fallback = 'Erro ocorrido') => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || 'Erro de validação').join(', ');
  }
  return detail || fallback;
};

const languageLabels = {
  pt: { label: 'Português', flag: '🇧🇷' },
  en: { label: 'English', flag: '🇺🇸' },
  es: { label: 'Español', flag: '🇪🇸' },
};

const EQUIPMENT_ROLE_VALUE = '__equipment_kiosk__';
const BOT_USERNAME_RE = /^[a-z0-9._-]{3,32}$/;

// Cadastro antigo não traz a chave; ausência = ativo (rollout aditivo do backend).
const isUserActive = (user) => user?.is_active !== false;
const isBotAccount = (user) => user?.account_type === 'bot' || user?.is_bot === true;

const UsersPage = () => {
  const { t } = useTranslation();
  const { user: currentUser, isAdmin, token } = useAuth();
  const { changePassword } = useAuth();
  const { formatDateTime } = useTimezone();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  // Reset de senha por admin: confirmacao + exibicao UNICA da senha temporaria.
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [tempPasswordInfo, setTempPasswordInfo] = useState(null); // { user, password }
  const [resettingPassword, setResettingPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cpfError, setCpfError] = useState('');
  const [approvingUser, setApprovingUser] = useState(null);
  const [rejectingUser, setRejectingUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [rbacRoles, setRbacRoles] = useState([]); // Papéis do RBAC
  const [equipments, setEquipments] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' });

  const [formData, setFormData] = useState({
    full_name: '',
    username: '', // CPF
    email: '',
    password: '',
    current_password: '',
    language: 'pt',
    role: 'user',
    account_type: 'human',
    equipment_id: '',
    session_timeout_minutes: null, // null = padrão; 0 = nunca; >0 = minutos
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, pendingRes, deptsRes, rolesRes, equipmentsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/auth/signup/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/auth/departments`).catch(() => ({ data: [] })),
        axios.get(`${API}/rbac/roles`).catch(() => ({ data: [] })), // Buscar papéis RBAC
        axios.get(`${API}/equipments`).catch(() => ({ data: [] })),
      ]);
      
      // Filtrar usuários ativos (aprovados ou sem status de aprovação)
      const activeUsers = usersRes.data.filter(u => 
        !u.approval_status || u.approval_status === 'approved'
      );
      setUsers(activeUsers);
      setPendingUsers(pendingRes.data || []);
      setDepartments(deptsRes.data || []);
      setRbacRoles(rolesRes.data || []);
      setEquipments(equipmentsRes.data || []);
      
      // Se há pendentes, mudar para aba de pendentes
      setActiveTab(prev => (
        pendingRes.data?.length > 0 && prev === 'active' ? 'pending' : prev
      ));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (user = null) => {
    setCpfError('');
    if (user) {
      const isEquipmentAccount = user.account_type === 'equipment_kiosk';
      const isBot = isBotAccount(user);
      setFormData({
        full_name: user.full_name,
        username: isEquipmentAccount || isBot ? (user.username || '') : formatCPF(user.username),
        email: user.email || '',
        password: '',
        current_password: '',
        language: user.language,
        role: isEquipmentAccount ? EQUIPMENT_ROLE_VALUE : (user.role || 'user'),
        account_type: isEquipmentAccount ? 'equipment_kiosk' : isBot ? 'bot' : 'human',
        equipment_id: user.equipment_id || '',
        session_timeout_minutes: user.session_timeout_minutes ?? null,
      });
      setSelectedUser(user);
    } else {
      setFormData({
        full_name: '',
        username: '',
        email: '',
        password: '',
        language: 'pt',
        role: 'user',
        account_type: 'human',
        equipment_id: '',
        session_timeout_minutes: null,
      });
      setSelectedUser(null);
    }
    setDialogOpen(true);
  };

  const handleCPFChange = (value) => {
    const formatted = formatCPF(value);
    setFormData({ ...formData, username: formatted });
    
    // Validate if complete
    const cleaned = cleanCPF(formatted);
    if (cleaned.length === 11) {
      if (!validateCPF(cleaned)) {
        setCpfError('CPF inválido');
      } else {
        setCpfError('');
      }
    } else {
      setCpfError('');
    }
  };

  const getEquipmentLabel = (equipment) => {
    if (!equipment) return '';
    return [equipment.tag, equipment.name].filter(Boolean).join(' - ');
  };

  const getSelectedEquipment = (equipmentId = formData.equipment_id) => (
    equipments.find((equipment) => equipment.id === equipmentId)
  );

  const handleRoleChange = (value) => {
    if (value === EQUIPMENT_ROLE_VALUE) {
      const nextData = {
        ...formData,
        role: EQUIPMENT_ROLE_VALUE,
        account_type: 'equipment_kiosk',
        username: '',
      };
      const selectedEquipment = getSelectedEquipment(nextData.equipment_id);
      if (!nextData.full_name && selectedEquipment?.tag) {
        nextData.full_name = `Equipamento ${selectedEquipment.tag}`;
      }
      setCpfError('');
      setFormData(nextData);
      return;
    }

    setFormData({
      ...formData,
      role: value,
      account_type: formData.account_type === 'bot' ? 'bot' : 'human',
      equipment_id: '',
    });
  };

  const handleAccountTypeChange = (value) => {
    if (value === 'equipment_kiosk') {
      handleRoleChange(EQUIPMENT_ROLE_VALUE);
      return;
    }
    if (value === 'bot') {
      setCpfError('');
      setFormData({
        ...formData,
        account_type: 'bot',
        equipment_id: '',
        role: formData.role === EQUIPMENT_ROLE_VALUE ? 'user' : formData.role,
        username: '',
      });
      return;
    }
    setFormData({
      ...formData,
      account_type: 'human',
      equipment_id: '',
      role: formData.role === EQUIPMENT_ROLE_VALUE ? 'user' : formData.role,
    });
  };

  const handleBotUsernameChange = (value) => {
    setFormData({ ...formData, username: value.toLowerCase() });
  };

  const handleEquipmentChange = (equipmentId) => {
    const selectedEquipment = getSelectedEquipment(equipmentId);
    setFormData({
      ...formData,
      equipment_id: equipmentId,
      full_name: formData.full_name || (selectedEquipment?.tag ? `Equipamento ${selectedEquipment.tag}` : ''),
    });
  };

  const isEquipmentForm = formData.account_type === 'equipment_kiosk';
  const isBotForm = formData.account_type === 'bot';

  const handleSave = async () => {
    if (!selectedUser) {
      if (isEquipmentForm) {
        if (!formData.equipment_id) {
          toast.error('Selecione o equipamento');
          return;
        }
      } else if (isBotForm) {
        const username = formData.username.trim().toLowerCase();
        if (!BOT_USERNAME_RE.test(username) || !/[a-z]/.test(username)) {
          toast.error('Nome de usuário inválido. Use 3 a 32 caracteres (a-z, 0-9, ponto, _ ou -) e ao menos uma letra');
          return;
        }
        if (!formData.password) {
          toast.error('Senha obrigatória');
          return;
        }
      } else {
        const cleaned = cleanCPF(formData.username);
        if (!validateCPF(cleaned)) {
          setCpfError('CPF inválido');
          toast.error('CPF inválido');
          return;
        }
      }
      // E-mail obrigatório para pessoa e equipamento; opcional para bot.
      const emailRequired = !isBotForm;
      if ((emailRequired || formData.email) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email || '')) {
        toast.error('E-mail inválido');
        return;
      }
    }

    setSaving(true);
    try {
      if (selectedUser) {
        const updateData = {
          full_name: formData.full_name,
          language: formData.language,
        };
        if (!isBotForm || formData.email) {
          updateData.email = formData.email;
        }
        const isSelf = selectedUser.id === currentUser?.id;
        if (formData.password) {
          if (isSelf) {
            // A PROPRIA senha muda so em /auth/change-password (senha atual +
            // token novo); via PUT a sessao atual seria revogada.
            if (!formData.current_password) {
              toast.error('Informe a senha atual para trocar a sua senha');
              setSaving(false);
              return;
            }
            await changePassword(formData.current_password, formData.password);
          } else {
            // Admin definindo senha de outro: o backend forca a troca no proximo acesso.
            updateData.password = formData.password;
          }
        }
        // Only admin can change roles
        if (isAdmin && formData.role && !isEquipmentForm) {
          updateData.role = formData.role;
        }
        // Tempo de sessão: ato administrativo. Enviamos sempre (null/0/minutos)
        // para permitir inclusive o reset ao padrão (null).
        if (isAdmin) {
          updateData.session_timeout_minutes = formData.session_timeout_minutes;
        }
        await axios.put(`${API}/users/${selectedUser.id}`, updateData);
      } else {
        const createPayload = {
          full_name: formData.full_name,
          password: formData.password,
          language: formData.language,
          role: isEquipmentForm ? 'user' : formData.role,
          account_type: isEquipmentForm ? 'equipment_kiosk' : isBotForm ? 'bot' : 'human',
        };
        if (!isBotForm || formData.email) {
          createPayload.email = formData.email;
        }

        if (isEquipmentForm) {
          createPayload.equipment_id = formData.equipment_id;
        } else if (isBotForm) {
          createPayload.username = formData.username.trim().toLowerCase();
        } else {
          createPayload.username = cleanCPF(formData.username);
        }

        const authToken = token || sessionStorage.getItem('octopus_token');
        await axios.post(`${API}/auth/register`, createPayload, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
      }
      toast.success(t('saveSuccess'));
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(getErrorMessage(error, t('errorOccurred')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success(t('deleteSuccess'));
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(getErrorMessage(error, t('errorOccurred')));
    }
  };

  const handleResetPassword = async (user) => {
    if (!user) return;
    setResettingPassword(true);
    try {
      const res = await axios.post(`${API}/users/${user.id}/reset-password`);
      // A senha temporaria so existe nesta resposta: mostramos uma vez e
      // descartamos ao fechar o dialogo (nao vai para estado global nem log).
      setTempPasswordInfo({ user, password: res.data?.temporary_password || '' });
      setResetDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error resetting password');
      toast.error(getErrorMessage(error, t('errorOccurred')));
    } finally {
      setResettingPassword(false);
    }
  };

  const closeTempPasswordDialog = () => {
    setTempPasswordInfo(null);
    setSelectedUser(null);
  };

  const copyTempPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPasswordInfo?.password || '');
      toast.success('Senha copiada');
    } catch (e) {
      toast.error('Não foi possível copiar');
    }
  };

  const handleToggleActive = async (user, nextActive) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: nextActive });
      toast.success(
        nextActive
          ? `${user.full_name} voltou a ter acesso`
          : `${user.full_name} foi inativado e perdeu o acesso`
      );
      setDeactivateDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user active state:', error);
      toast.error(getErrorMessage(error, t('errorOccurred')));
    }
  };

  const handleApproveUser = async (user) => {
    setApprovingUser(user.id);
    try {
      await axios.post(`${API}/auth/signup/approve-manual/${user.id}`);
      toast.success(`Usuário ${user.full_name} aprovado com sucesso!`);
      fetchUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error(getErrorMessage(error, 'Erro ao aprovar usuário'));
    } finally {
      setApprovingUser(null);
    }
  };

  const handleRejectUser = async (user) => {
    setRejectingUser(user.id);
    try {
      await axios.post(`${API}/auth/signup/reject-manual/${user.id}`);
      toast.success(`Cadastro de ${user.full_name} rejeitado`);
      fetchUsers();
    } catch (error) {
      console.error('Error rejecting user:', error);
      toast.error(getErrorMessage(error, 'Erro ao rejeitar usuário'));
    } finally {
      setRejectingUser(null);
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return formatDateTime(dateStr, { omitSeconds: true });
    } catch {
      return dateStr;
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    if (phone.length === 11) {
      return `(${phone.slice(0,2)}) ${phone.slice(2,7)}-${phone.slice(7)}`;
    }
    return phone;
  };

  // Get display name for role
  const getRoleDisplayName = (role) => {
    if (!role) return '-';
    // Find role in RBAC roles
    const rbacRole = rbacRoles.find(r => r.name.toLowerCase() === role.toLowerCase());
    return rbacRole?.name || role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getUserRoleDisplayName = (user) => (
    user.account_type === 'equipment_kiosk' ? 'Equipamento' : getRoleDisplayName(user.role)
  );

  const formatUserLogin = (user) => (
    user.account_type === 'equipment_kiosk' || isBotAccount(user)
      ? (user.username || '')
      : formatCPF(user.username || '')
  );

  const renderBotBadge = (user) => (
    isBotAccount(user) ? (
      <Badge variant="outline" className="gap-1" data-testid={`user-bot-badge-${user.id}`}>
        <Bot className="h-3 w-3" />
        Bot
      </Badge>
    ) : null
  );

  // Sort function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const filteredUsers = users
    .filter(user =>
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      
      if (sortConfig.direction === 'asc') {
        return aVal.toString().localeCompare(bVal.toString());
      }
      return bVal.toString().localeCompare(aVal.toString());
    });

  const renderUserMobileCard = (user) => (
    <Card
      key={user.id}
      data-testid={`mobile-user-card-${user.id}`}
      className={isUserActive(user) ? undefined : 'opacity-60'}
    >
      <CardContent className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate font-semibold">{user.full_name}</h2>
              {user.id === currentUser?.id && <Badge variant="outline">Você</Badge>}
              {renderBotBadge(user)}
              {!isUserActive(user) && (
                <Badge variant="outline" className="border-destructive text-destructive">
                  Inativo
                </Badge>
              )}
            </div>
            <p className="font-mono text-xs text-muted-foreground">{formatUserLogin(user)}</p>
            <p className="text-sm text-muted-foreground">{user.email || '-'}</p>
          </div>
          <Badge variant={user.role === 'admin' ? 'destructive' : 'secondary'} className="shrink-0 text-xs">
            {getUserRoleDisplayName(user)}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{languageLabels[user.language]?.flag}</span>
          <span>{languageLabels[user.language]?.label}</span>
        </div>
        <div className={`grid gap-2 ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <Button variant="outline" size="sm" className="min-h-10 gap-2" onClick={() => handleOpenDialog(user)} data-testid={`mobile-edit-user-${user.id}`}>
            <Pencil className="h-4 w-4" /> Editar
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="min-h-10 gap-2"
              onClick={() => {
                setSelectedUser(user);
                if (isUserActive(user)) {
                  setDeactivateDialogOpen(true);
                } else {
                  handleToggleActive(user, true);
                }
              }}
              disabled={user.id === currentUser?.id}
              data-testid={`mobile-toggle-active-user-${user.id}`}
            >
              {isUserActive(user)
                ? <><UserX className="h-4 w-4 text-destructive" /> Inativar</>
                : <><UserCheck className="h-4 w-4 text-green-600" /> Reativar</>}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="min-h-10 gap-2 text-destructive"
            onClick={() => {
              setSelectedUser(user);
              setDeleteDialogOpen(true);
            }}
            disabled={user.id === currentUser?.id}
            data-testid={`mobile-delete-user-${user.id}`}
          >
            <Trash2 className="h-4 w-4" /> Excluir
          </Button>
          {isAdmin && !isBotAccount(user) && (
            <Button
              variant="outline"
              size="sm"
              className="min-h-10 gap-2"
              onClick={() => {
                setSelectedUser(user);
                setResetDialogOpen(true);
              }}
              disabled={user.id === currentUser?.id}
              data-testid={`mobile-reset-password-user-${user.id}`}
            >
              <KeyRound className="h-4 w-4" /> Resetar senha
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('users')}</h1>
          <p className="text-muted-foreground">{t('administration')}</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="add-user-btn">
          <Plus className="w-4 h-4" />
          {t('addUser')}
        </Button>
      </div>

      {/* Tabs for Active and Pending Users */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active" className="gap-2">
            <Users className="w-4 h-4" />
            Usuários Ativos
            <Badge variant="secondary" className="ml-1">{users.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="w-4 h-4" />
            Aguardando Aprovação
            {pendingUsers.length > 0 && (
              <Badge variant="destructive" className="ml-1 animate-pulse">{pendingUsers.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Active Users Tab */}
        <TabsContent value="active" className="space-y-4">
          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="user-search"
              />
            </div>
          </div>

          {/* Active Users Table */}
          <ResponsiveDataList
            items={filteredUsers}
            renderCard={renderUserMobileCard}
            empty={
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  {loading ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <>
                      <Users className="mb-2 h-8 w-8 opacity-50" />
                      {t('noUsers')}
                    </>
                  )}
                </CardContent>
              </Card>
            }
          >
          <Card>
            <CardContent className="p-0">
              <ScrollArea className="w-full">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('full_name')}
                      >
                        {t('fullName')} {getSortIcon('full_name')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('role')}
                      >
                        Papel {getSortIcon('role')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('username')}
                      >
                        Login/CPF {getSortIcon('username')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('email')}
                      >
                        {t('email')} {getSortIcon('email')}
                      </TableHead>
                      <TableHead>{t('language')}</TableHead>
                      <TableHead className="text-right">{t('edit')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          {t('noUsers')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => (
                        <TableRow
                          key={user.id}
                          data-testid={`user-row-${user.id}`}
                          className={isUserActive(user) ? undefined : 'opacity-60'}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {user.full_name}
                              {user.id === currentUser?.id && (
                                <Badge variant="outline">Você</Badge>
                              )}
                              {renderBotBadge(user)}
                              {!isUserActive(user) && (
                                <Badge variant="outline" className="border-destructive text-destructive">
                                  Inativo
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={user.role === 'admin' ? 'destructive' : 'secondary'}
                              className="text-xs"
                            >
                              {getUserRoleDisplayName(user)}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{formatUserLogin(user)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {user.email || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{languageLabels[user.language]?.flag}</span>
                              <span>{languageLabels[user.language]?.label}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog(user)}
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {isAdmin && !isBotAccount(user) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Resetar senha"
                                  aria-label="Resetar senha"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setResetDialogOpen(true);
                                  }}
                                  disabled={user.id === currentUser?.id}
                                  data-testid={`reset-password-user-${user.id}`}
                                >
                                  <KeyRound className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title={isUserActive(user) ? 'Inativar usuário' : 'Reativar usuário'}
                                  onClick={() => {
                                    setSelectedUser(user);
                                    if (isUserActive(user)) {
                                      setDeactivateDialogOpen(true);
                                    } else {
                                      handleToggleActive(user, true);
                                    }
                                  }}
                                  disabled={user.id === currentUser?.id}
                                  data-testid={`toggle-active-user-${user.id}`}
                                >
                                  {isUserActive(user)
                                    ? <UserX className="w-4 h-4 text-destructive" />
                                    : <UserCheck className="w-4 h-4 text-green-600" />}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDeleteDialogOpen(true);
                                }}
                                disabled={user.id === currentUser?.id}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
          </ResponsiveDataList>
        </TabsContent>

        {/* Pending Users Tab */}
        <TabsContent value="pending" className="space-y-4">
          {pendingUsers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Nenhum cadastro pendente</h3>
                <p className="text-muted-foreground">
                  Todos os cadastros foram processados.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingUsers.map((user) => (
                <Card key={user.id} className="border-yellow-500/50">
                  <CardHeader className="pb-2">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-yellow-500" />
                          {user.full_name}
                        </CardTitle>
                        <CardDescription>
                          Solicitado em {formatDate(user.created_at)}
                        </CardDescription>
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:flex">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleRejectUser(user)}
                          disabled={rejectingUser === user.id}
                          data-testid={`reject-user-${user.id}`}
                        >
                          {rejectingUser === user.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          Rejeitar
                        </Button>
                        <Button
                          size="sm"
                          className="gap-2 bg-green-600 hover:bg-green-700"
                          onClick={() => handleApproveUser(user)}
                          disabled={approvingUser === user.id}
                          data-testid={`approve-user-${user.id}`}
                        >
                          {approvingUser === user.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                          Aprovar
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">CPF:</span>
                        <span className="font-mono">{formatCPF(user.username)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{user.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{formatPhone(user.phone)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{formatDate(user.birth_date)}</span>
                      </div>
                      {user.department_id && (
                        <div className="flex items-center gap-2 col-span-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{getDepartmentName(user.department_id)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? t('editUser') : t('addUser')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">{t('fullName')}</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                data-testid="user-fullname-input"
              />
            </div>

            {isAdmin && !selectedUser && (
              <div className="space-y-2">
                <Label>Tipo de conta</Label>
                <Select
                  value={formData.account_type}
                  onValueChange={handleAccountTypeChange}
                >
                  <SelectTrigger data-testid="user-account-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="human">Pessoa</SelectItem>
                    <SelectItem value="equipment_kiosk">Equipamento</SelectItem>
                    <SelectItem value="bot">Bot / serviço</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isAdmin && (
              <div className="space-y-2">
                <Label>Papel</Label>
                <Select
                  value={formData.role}
                  onValueChange={handleRoleChange}
                  disabled={selectedUser?.account_type === 'equipment_kiosk'}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EQUIPMENT_ROLE_VALUE}>Equipamento</SelectItem>
                    {rbacRoles.length === 0 ? (
                      <>
                        <SelectItem value="user">Usuário</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                      </>
                    ) : (
                      rbacRoles
                        .filter((role) => role.code !== 'OPERADOR_EQUIPAMENTO')
                        .map((role) => (
                          <SelectItem key={role.id || role.name} value={role.name.toLowerCase()}>
                            {role.name}
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {isAdmin && (
              <div className="space-y-2">
                <Label>Tempo de sessão</Label>
                <Select
                  value={(formData.session_timeout_minutes === null || formData.session_timeout_minutes === undefined) ? 'default' : String(formData.session_timeout_minutes)}
                  onValueChange={(v) => setFormData({ ...formData, session_timeout_minutes: v === 'default' ? null : parseInt(v, 10) })}
                >
                  <SelectTrigger data-testid="user-session-timeout-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Padrão do sistema (2 h)</SelectItem>
                    <SelectItem value="0">Nunca expira</SelectItem>
                    <SelectItem value="30">30 minutos</SelectItem>
                    <SelectItem value="60">1 hora</SelectItem>
                    <SelectItem value="240">4 horas</SelectItem>
                    <SelectItem value="480">8 horas</SelectItem>
                    <SelectItem value="1440">24 horas</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Minutos de inatividade até o logout automático. "Nunca" mantém o usuário sempre logado.
                </p>
              </div>
            )}

            {isEquipmentForm ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="username">CPF</Label>
                  <Input
                    id="username"
                    value=""
                    disabled
                    placeholder="Não aplicável para equipamento"
                    className="bg-secondary/50"
                    data-testid="user-cpf-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Equipamento</Label>
                  <Select
                    value={formData.equipment_id}
                    onValueChange={handleEquipmentChange}
                    disabled={Boolean(selectedUser)}
                  >
                    <SelectTrigger data-testid="user-equipment-select">
                      <SelectValue placeholder="Selecione o equipamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipments.map((equipment) => (
                        <SelectItem key={equipment.id} value={equipment.id}>
                          {getEquipmentLabel(equipment)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedUser && (
                    <p className="text-xs text-muted-foreground">Equipamento vinculado não pode ser alterado por este formulário</p>
                  )}
                </div>
              </>
            ) : isBotForm ? (
              <div className="space-y-2">
                <Label htmlFor="bot-username">Usuário</Label>
                <Input
                  id="bot-username"
                  value={formData.username}
                  onChange={(e) => handleBotUsernameChange(e.target.value)}
                  placeholder="ex.: jethro"
                  maxLength={32}
                  disabled={Boolean(selectedUser)}
                  className={selectedUser ? 'bg-secondary/50' : ''}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid="user-bot-username-input"
                />
                <p className="text-xs text-muted-foreground">
                  {selectedUser
                    ? 'Usuário não pode ser alterado'
                    : '3 a 32 caracteres: a-z, 0-9, ponto, _ ou -. Sem CPF e sem e-mail.'}
                </p>
              </div>
            ) : !selectedUser ? (
              <div className="space-y-2">
                <Label htmlFor="username">CPF</Label>
                <div className="relative">
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleCPFChange(e.target.value)}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    className={cpfError ? 'border-destructive' : ''}
                    data-testid="user-cpf-input"
                  />
                  {cpfError && (
                    <div className="flex items-center gap-1 mt-1 text-destructive text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {cpfError}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>CPF</Label>
                <Input
                  value={formData.username}
                  disabled
                  className="bg-secondary/50"
                />
                <p className="text-xs text-muted-foreground">CPF não pode ser alterado</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('email')} {isBotForm ? '(opcional)' : '*'}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={isBotForm ? 'opcional' : 'usuario@email.com'}
                required={!selectedUser && !isBotForm}
                data-testid="user-email-input"
              />
              <p className="text-xs text-muted-foreground">
                {isBotForm ? 'Opcional para conta bot. O login é o usuário.' : 'Usado para recuperação de senha'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                {t('password')} {selectedUser && '(deixe em branco para manter)'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={selectedUser ? '••••••••' : ''}
                data-testid="user-password-input"
              />
            </div>

            {selectedUser && selectedUser.id === currentUser?.id && formData.password && (
              <div className="space-y-2">
                <Label htmlFor="current_password">Senha atual</Label>
                <Input
                  id="current_password"
                  type="password"
                  autoComplete="current-password"
                  value={formData.current_password}
                  onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                  data-testid="user-current-password-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('language')}</Label>
              <Select
                value={formData.language}
                onValueChange={(value) => setFormData({ ...formData, language: value })}
              >
                <SelectTrigger data-testid="user-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pt">
                    <div className="flex items-center gap-2">
                      🇧🇷 {t('portuguese')}
                    </div>
                  </SelectItem>
                  <SelectItem value="en">
                    <div className="flex items-center gap-2">
                      🇺🇸 {t('english')}
                    </div>
                  </SelectItem>
                  <SelectItem value="es">
                    <div className="flex items-center gap-2">
                      🇪🇸 {t('spanish')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteUser')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate Confirmation */}
      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar usuário</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser?.full_name} perde o acesso imediatamente — inclusive a sessão que
              estiver aberta agora — e deixa de receber boletins. O cadastro e o histórico
              permanecem, e o acesso pode ser devolvido a qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleToggleActive(selectedUser, false)}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-deactivate-user"
            >
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset de senha: confirmacao */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resetar senha</AlertDialogTitle>
            <AlertDialogDescription>
              Uma senha temporária será gerada para {selectedUser?.full_name}. As sessões abertas
              dele serão encerradas e ele precisará definir uma nova senha no próximo acesso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleResetPassword(selectedUser); }}
              disabled={resettingPassword}
              data-testid="confirm-reset-password"
            >
              {resettingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resetar senha'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Senha temporaria: exibida UMA unica vez */}
      <Dialog open={!!tempPasswordInfo} onOpenChange={(open) => { if (!open) closeTempPasswordDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Senha temporária de {tempPasswordInfo?.user?.full_name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Esta senha é exibida apenas uma vez. Repasse ao usuário por um canal seguro;
            ele será obrigado a trocá-la no primeiro acesso.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded bg-muted px-3 py-2 font-mono text-base" data-testid="temp-password-value">
              {tempPasswordInfo?.password}
            </code>
            <Button variant="outline" size="icon" onClick={copyTempPassword} aria-label="Copiar senha">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={closeTempPasswordDialog} data-testid="close-temp-password">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
